/**
 * Wizard State Machine Service
 *
 * Manages the state and transitions for the 6-step device update wizard.
 * Implements a finite state machine with validation guards and error recovery.
 */

import {
  WizardStep,
  WizardState,
  StepState,
  StateAction,
  StateActionType,
  UpdaterError,
  ErrorCode,
  RecoveryAction,
  WIZARD_STEPS_ORDER,
} from '../types/wizard-types';
import { ILogger } from '../types/imx-types';

/**
 * State change listener callback
 */
export type StateChangeListener = (state: WizardState) => void;

/**
 * Transition validation result
 */
export interface TransitionValidation {
  valid: boolean;
  error?: UpdaterError;
}

/**
 * Wizard State Machine
 *
 * Manages wizard state transitions with validation and error handling.
 * Uses an observable pattern for state change notifications.
 */
export class WizardStateMachine {
  private state: WizardState;
  private listeners: Set<StateChangeListener> = new Set();

  constructor(private logger: ILogger, initialState?: Partial<WizardState>) {
    // Initialize with default state
    this.state = this.getInitialState();

    // Apply any custom initial state
    if (initialState) {
      this.state = { ...this.state, ...initialState };
    }

    this.logger.debug('Wizard state machine initialized', this.state);
  }

  // ============================================================================
  // State Access
  // ============================================================================

  /**
   * Get current wizard state (immutable copy)
   */
  getState(): Readonly<WizardState> {
    return { ...this.state };
  }

  /**
   * Get current step
   */
  getCurrentStep(): WizardStep {
    return this.state.currentStep;
  }

  /**
   * Get step state for a specific step
   */
  getStepState(step: WizardStep): StepState {
    return this.state.stepStates.get(step) || StepState.PENDING;
  }

  /**
   * Check if wizard is completed
   */
  isCompleted(): boolean {
    return this.state.completed;
  }

  /**
   * Check if there's an active error
   */
  hasError(): boolean {
    return this.state.error !== null;
  }

  // ============================================================================
  // State Mutations
  // ============================================================================

  /**
   * Dispatch a state action
   */
  dispatch(action: StateAction): void {
    this.logger.debug('Dispatching action', action);

    const previousState = { ...this.state };

    switch (action.type) {
      case StateActionType.SET_STEP:
        this.handleSetStep(action.step);
        break;

      case StateActionType.NEXT_STEP:
        this.handleNextStep();
        break;

      case StateActionType.PREVIOUS_STEP:
        this.handlePreviousStep();
        break;

      case StateActionType.SKIP_TO_STEP:
        this.handleSkipToStep(action.step, action.markCompletedSteps);
        break;

      case StateActionType.SET_EID:
        this.state.eid = action.eid;
        break;

      case StateActionType.VALIDATE_EID:
        this.state.eidValidated = action.valid;
        this.state.canProceed = action.valid;
        if (action.valid) {
          this.setStepState(WizardStep.EID_ENTRY, StepState.COMPLETED);
        }
        break;

      case StateActionType.SET_DEVICE_INFO:
        this.state.deviceInfo = action.deviceInfo;
        break;

      case StateActionType.SET_DEVICE_CONNECTED:
        this.state.deviceConnected = action.connected;
        if (action.connected) {
          this.setStepState(WizardStep.DEVICE_CONNECTION, StepState.COMPLETED);
          this.state.canProceed = true;
        }
        break;

      case StateActionType.SET_PROGRESS:
        this.state.currentProgress = action.percent;
        if (action.message) {
          this.state.currentProgressMessage = action.message;
        }
        break;

      case StateActionType.SET_STEP_STATE:
        this.setStepState(action.step, action.state);
        break;

      case StateActionType.SET_ERROR:
        this.state.error = action.error;
        this.state.canProceed = false;
        this.setStepState(this.state.currentStep, StepState.ERROR);
        this.updateNavigationState();
        this.logger.error('Wizard error', action.error);
        break;

      case StateActionType.CLEAR_ERROR:
        this.state.error = null;
        this.updateNavigationState();
        break;

      case StateActionType.RETRY_CURRENT_STEP:
        this.handleRetry();
        break;

      case StateActionType.RESET:
        this.state = this.getInitialState();
        this.logger.info('Wizard reset');
        break;

      case StateActionType.COMPLETE:
        this.state.completed = true;
        this.state.currentProgress = 100;
        this.state.currentProgressMessage = 'Update complete!';
        this.setStepState(WizardStep.FINALIZATION, StepState.COMPLETED);
        this.logger.success('Wizard completed successfully');
        break;

      default:
        this.logger.warning('Unknown action type', action);
    }

    // Notify listeners if state changed
    if (this.hasStateChanged(previousState, this.state)) {
      this.notifyListeners();
    }
  }

  // ============================================================================
  // Step Transitions
  // ============================================================================

  /**
   * Transition to a specific step
   */
  transitionTo(step: WizardStep): void {
    const validation = this.validateTransition(this.state.currentStep, step);

    if (!validation.valid) {
      this.logger.error('Invalid transition', {
        from: this.state.currentStep,
        to: step,
      });
      if (validation.error) {
        this.dispatch({
          type: StateActionType.SET_ERROR,
          error: validation.error,
        });
      }
      return;
    }

    this.dispatch({ type: StateActionType.SET_STEP, step });
  }

  /**
   * Move to next step
   */
  nextStep(): void {
    const currentIndex = WIZARD_STEPS_ORDER.indexOf(this.state.currentStep);
    if (currentIndex < WIZARD_STEPS_ORDER.length - 1) {
      const nextStep = WIZARD_STEPS_ORDER[currentIndex + 1];
      this.transitionTo(nextStep);
    } else {
      this.logger.warning('Already at last step');
    }
  }

  /**
   * Move to previous step
   */
  previousStep(): void {
    const currentIndex = WIZARD_STEPS_ORDER.indexOf(this.state.currentStep);
    if (currentIndex > 0) {
      const prevStep = WIZARD_STEPS_ORDER[currentIndex - 1];
      this.dispatch({ type: StateActionType.SET_STEP, step: prevStep });
    } else {
      this.logger.warning('Already at first step');
    }
  }

  /**
   * Retry current step
   */
  retry(): void {
    this.dispatch({ type: StateActionType.RETRY_CURRENT_STEP });
  }

  /**
   * Reset wizard to initial state
   */
  reset(): void {
    this.dispatch({ type: StateActionType.RESET });
  }

  // ============================================================================
  // Validation
  // ============================================================================

  /**
   * Validate transition from one step to another
   */
  validateTransition(from: WizardStep, to: WizardStep): TransitionValidation {
    const fromIndex = WIZARD_STEPS_ORDER.indexOf(from);
    const toIndex = WIZARD_STEPS_ORDER.indexOf(to);

    // Allow backward navigation
    if (toIndex < fromIndex) {
      return { valid: true };
    }

    // Only allow one step forward at a time
    if (toIndex > fromIndex + 1) {
      return {
        valid: false,
        error: new UpdaterError(
          'Cannot skip steps',
          ErrorCode.UNKNOWN_ERROR,
          from,
          [
            {
              action: RecoveryAction.START_OVER,
              label: 'Start Over',
              description: 'Restart the update process',
              order: 1,
            },
          ],
          `Attempted to jump from ${from} to ${to}`
        ),
      };
    }

    // Validate prerequisites for forward transitions
    switch (to) {
      case WizardStep.DEVICE_CONNECTION:
        if (!this.state.eidValidated) {
          return {
            valid: false,
            error: new UpdaterError(
              'EID must be validated before connecting device',
              ErrorCode.EID_NOT_FOUND,
              from,
              [
                {
                  action: RecoveryAction.RETRY_STEP,
                  label: 'Enter Valid EID',
                  description: 'Go back and enter a valid Equipment ID',
                  order: 1,
                },
              ]
            ),
          };
        }
        break;

      case WizardStep.BOOTLOADER_TRANSFER:
        if (!this.state.deviceConnected) {
          return {
            valid: false,
            error: new UpdaterError(
              'Device must be connected before transferring bootloader',
              ErrorCode.DEVICE_NOT_DETECTED,
              from,
              [
                {
                  action: RecoveryAction.CHECK_CONNECTION,
                  label: 'Check Connection',
                  description:
                    'Ensure device is connected and in recovery mode',
                  order: 1,
                },
                {
                  action: RecoveryAction.RETRY_STEP,
                  label: 'Retry Connection',
                  description: 'Attempt to reconnect to the device',
                  order: 2,
                },
              ]
            ),
          };
        }
        break;

      case WizardStep.EASY_INSTALLER_LOADING:
        if (
          this.getStepState(WizardStep.BOOTLOADER_TRANSFER) !==
          StepState.COMPLETED
        ) {
          return {
            valid: false,
            error: new UpdaterError(
              'Bootloader transfer must complete before loading Easy Installer',
              ErrorCode.BOOTLOADER_TRANSFER_FAILED,
              from,
              [
                {
                  action: RecoveryAction.RETRY_STEP,
                  label: 'Retry Bootloader Transfer',
                  description: 'Attempt to transfer bootloader again',
                  order: 1,
                },
                {
                  action: RecoveryAction.POWER_CYCLE,
                  label: 'Power Cycle Device',
                  description: 'Turn device off and on, then start over',
                  order: 2,
                },
              ]
            ),
          };
        }
        break;

      case WizardStep.FIRMWARE_FLASHING:
        if (
          this.getStepState(WizardStep.EASY_INSTALLER_LOADING) !==
          StepState.COMPLETED
        ) {
          return {
            valid: false,
            error: new UpdaterError(
              'Easy Installer must load before flashing firmware',
              ErrorCode.EASY_INSTALLER_TRANSFER_FAILED,
              from,
              [
                {
                  action: RecoveryAction.RETRY_STEP,
                  label: 'Retry Easy Installer Loading',
                  description: 'Attempt to load Easy Installer again',
                  order: 1,
                },
                {
                  action: RecoveryAction.START_OVER,
                  label: 'Start Over',
                  description: 'Restart the entire update process',
                  order: 2,
                },
              ]
            ),
          };
        }
        break;

      case WizardStep.FINALIZATION:
        if (
          this.getStepState(WizardStep.FIRMWARE_FLASHING) !==
          StepState.COMPLETED
        ) {
          return {
            valid: false,
            error: new UpdaterError(
              'Firmware must be flashed before finalization',
              ErrorCode.FIRMWARE_FLASH_FAILED,
              from,
              [
                {
                  action: RecoveryAction.RETRY_STEP,
                  label: 'Retry Firmware Flash',
                  description: 'Attempt to flash firmware again',
                  order: 1,
                },
                {
                  action: RecoveryAction.CONTACT_SUPPORT,
                  label: 'Contact Support',
                  description: 'Get help from technical support',
                  order: 2,
                },
              ]
            ),
          };
        }
        break;
    }

    return { valid: true };
  }

  /**
   * Check if transition is valid
   */
  canTransitionTo(step: WizardStep): boolean {
    return this.validateTransition(this.state.currentStep, step).valid;
  }

  // ============================================================================
  // Event Listeners
  // ============================================================================

  /**
   * Subscribe to state changes
   */
  subscribe(listener: StateChangeListener): () => void {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    const currentState = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(currentState);
      } catch (error) {
        this.logger.error('Error in state change listener', error);
      }
    });
  }

  // ============================================================================
  // Private Helpers
  // ============================================================================

  /**
   * Get initial wizard state
   */
  private getInitialState(): WizardState {
    const stepStates = new Map<WizardStep, StepState>();
    WIZARD_STEPS_ORDER.forEach((step, index) => {
      stepStates.set(step, index === 0 ? StepState.ACTIVE : StepState.PENDING);
    });

    return {
      currentStep: WizardStep.EID_ENTRY,
      stepStates,
      eid: null,
      eidValidated: false,
      deviceInfo: null,
      deviceConnected: false,
      currentProgress: 0,
      currentProgressMessage: '',
      error: null,
      canProceed: false,
      canGoBack: false,
      completed: false,
    };
  }

  /**
   * Handle SET_STEP action
   */
  private handleSetStep(step: WizardStep): void {
    this.state.currentStep = step;
    this.setStepState(step, StepState.ACTIVE);
    this.updateNavigationState();
    this.logger.info(`Transitioned to step: ${step}`);
  }

  /**
   * Handle NEXT_STEP action
   */
  private handleNextStep(): void {
    const validation = this.validateTransition(
      this.state.currentStep,
      this.getNextStep()
    );

    if (validation.valid) {
      this.nextStep();
    } else if (validation.error) {
      this.dispatch({
        type: StateActionType.SET_ERROR,
        error: validation.error,
      });
    }
  }

  /**
   * Handle PREVIOUS_STEP action
   */
  private handlePreviousStep(): void {
    if (this.state.canGoBack) {
      this.previousStep();
    }
  }

  /**
   * Handle RETRY action
   */
  private handleRetry(): void {
    const currentStep = this.state.currentStep;
    this.state.error = null;
    this.setStepState(currentStep, StepState.ACTIVE);
    this.state.currentProgress = 0;
    this.state.currentProgressMessage = '';
    this.updateNavigationState();
    this.logger.info(`Retrying step: ${currentStep}`);
  }

  /**
   * Handle SKIP_TO_STEP action
   * Used for resuming interrupted updates (e.g., when device is already in U-Boot mode)
   */
  private handleSkipToStep(
    targetStep: WizardStep,
    markCompletedSteps: WizardStep[]
  ): void {
    // Mark specified steps as completed
    markCompletedSteps.forEach((step) => {
      this.setStepState(step, StepState.COMPLETED);
    });

    // Transition to target step
    this.state.currentStep = targetStep;
    this.setStepState(targetStep, StepState.ACTIVE);
    this.state.currentProgress = 0;
    this.state.currentProgressMessage = '';
    this.updateNavigationState();
    this.logger.info(
      `Skipped to step: ${targetStep} (marked completed: ${markCompletedSteps.join(
        ', '
      )})`
    );
  }

  /**
   * Set state for a specific step
   */
  private setStepState(step: WizardStep, state: StepState): void {
    this.state.stepStates.set(step, state);
  }

  /**
   * Update navigation state based on current context
   */
  private updateNavigationState(): void {
    const currentIndex = WIZARD_STEPS_ORDER.indexOf(this.state.currentStep);

    // Can go back if not on first step and no error
    this.state.canGoBack = currentIndex > 0 && !this.hasError();

    // Can proceed based on current step requirements
    switch (this.state.currentStep) {
      case WizardStep.EID_ENTRY:
        this.state.canProceed = this.state.eidValidated;
        break;

      case WizardStep.DEVICE_CONNECTION:
        this.state.canProceed = this.state.deviceConnected;
        break;

      default:
        // For other steps, check if current step is completed
        this.state.canProceed =
          this.getStepState(this.state.currentStep) === StepState.COMPLETED;
    }
  }

  /**
   * Get next step in sequence
   */
  private getNextStep(): WizardStep {
    const currentIndex = WIZARD_STEPS_ORDER.indexOf(this.state.currentStep);
    return WIZARD_STEPS_ORDER[
      Math.min(currentIndex + 1, WIZARD_STEPS_ORDER.length - 1)
    ];
  }

  /**
   * Check if state has changed (deep comparison)
   */
  private hasStateChanged(prev: WizardState, current: WizardState): boolean {
    // Simple implementation - compare key properties
    return (
      prev.currentStep !== current.currentStep ||
      prev.eid !== current.eid ||
      prev.eidValidated !== current.eidValidated ||
      prev.deviceConnected !== current.deviceConnected ||
      prev.currentProgress !== current.currentProgress ||
      prev.error !== current.error ||
      prev.completed !== current.completed ||
      prev.canProceed !== current.canProceed ||
      prev.canGoBack !== current.canGoBack
    );
  }
}
